import React, { useRef } from "react";
import { CircleX } from "lucide-react";
import { AutocompleteSuggestion } from "../../../../api/locationAutocomplete";
import SuggestionsDropdown from "./SuggestionsDropdown";

interface SearchInputWithAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (e?: React.FormEvent) => void;
  onClear?: () => void; // New prop for handling clear action
  placeholder?: string;
  borderColor?: string;
  suggestions: AutocompleteSuggestion[];
  isSuggestionsLoading: boolean;
  showSuggestions: boolean;
  setShowSuggestions: (show: boolean) => void;
  selectedSuggestionIndex: number;
  setSelectedSuggestionIndex: (index: number) => void;
  handleSuggestionSelect: (suggestion: AutocompleteSuggestion) => void;
  handleSearchKeyDown: (e: React.KeyboardEvent) => void;
}

export const SearchInputWithAutocomplete: React.FC<
  SearchInputWithAutocompleteProps
> = ({
  value,
  onChange,
  onSubmit,
  onClear,
  placeholder = "Search Anything (e.g. modern 3 bedroom house with a pool)",
  borderColor = "#fff",
  suggestions,
  isSuggestionsLoading,
  showSuggestions,
  setShowSuggestions,
  selectedSuggestionIndex,
  handleSuggestionSelect,
  handleSearchKeyDown,
}) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !showSuggestions) {
      e.preventDefault();
      onSubmit(e as unknown as React.FormEvent);
    } else {
      handleSearchKeyDown(e);
    }
  };

  return (
    <div className="relative w-full">
      <div className="relative">
        <input
          ref={searchInputRef}
          type="text"
          value={value}
          onChange={(e) => {
            const newValue = e.target.value;
            onChange(newValue);

            if (newValue === "" && onClear) {
              onClear();
            }
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (value && value.length >= 2) {
              setShowSuggestions(true);
            }
          }}
          onBlur={(e) => {
            // Don't hide suggestions if clicking on the suggestions dropdown
            if (
              !e.relatedTarget ||
              !suggestionsRef.current?.contains(e.relatedTarget as Node)
            ) {
              // Small delay to allow for suggestion selection
              setTimeout(() => setShowSuggestions(false), 150);
            }
          }}
          placeholder={placeholder}
          className="w-full py-2 px-4  3xl:py-3 text-sm border rounded-lg transition-colors shadow-sm focus:ring-2 focus:border-primary"
          style={{
            ["--tw-ring-color" as string]: "var(--primary)",
            ["--tw-ring-offset-width" as string]: "0px",
            ["--tw-ring-width" as string]: "2px",
            borderColor,
          }}
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              // If onClear is provided, use it (parent component handles clearing logic)
              if (onClear) {
                onClear();
              }
            }}
            className="absolute inset-y-0 right-4 flex items-center bg-transparent p-0"
            style={{ outline: "none", boxShadow: "none" }}
          >
            <CircleX className="w-4 h-4 text-gray-700 hover:text-red-700" />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions &&
        (suggestions.length > 0 ||
          isSuggestionsLoading ||
          (value && value.length >= 2)) && (
          <SuggestionsDropdown
            suggestions={suggestions}
            isLoading={isSuggestionsLoading}
            selectedIndex={selectedSuggestionIndex}
            query={value}
            onSelect={handleSuggestionSelect}
            suggestionsRef={suggestionsRef}
          />
        )}
    </div>
  );
};

export default SearchInputWithAutocomplete;
